// `unnest` pipeline verb — flatten nested-dataset column(s) into
// outer rows.
//
// Surface:
//
//   (unnest [oil-prod])                       ; single column
//   (unnest [oil-prod] :as [t v])             ; single, rename inner
//   (unnest [oil-prod gas-prod] :as [day o g]); multi, zip-aligned
//
// Single-column unnest (PHASE_4_PREP.md D11.3 — unchanged): for each
// outer row the cell is materialised and each inner row emitted as a
// new output row. `:as` is positional rename over the inner schema.
//
// Multi-column unnest (D11.3): the listed columns must share an
// identical LEADING inner column — the grid key (`bucket`, produced
// by per-cell `time-bucket`). Per outer row the columns are
// zip-aligned by that key: the UNION of key values across the listed
// columns (ascending), with a missing `(column, key)` null-filled
// (D11.1). Output is the shared key once, then each column's
// non-key inner columns in vector order. `:as` (optional) renames
// positionally over `[key, …col0-nonkey, …col1-nonkey]`; its arity
// is `1 + Σ(non-key inner cols per column)`.

import { z } from 'zod';

import type { SourcePos } from '../../../ast.ts';
import { ShapeError, TypeMismatchError, UnnestableColumnTypeError, UnnestCollisionError } from '../../../errors.ts';
import { materializeIfLazy } from '../../../materialize.ts';
import { opCallPos } from '../../../normalize.ts';
import { type PropagateSchemaCtx, pipelineVerbs, type SpecialVerbCtx } from '../../../op-meta.ts';
import { materializedToRows } from '../../../runtime/arquero.ts';
import {
  type ColumnType,
  columnTypesEqual,
  describeColumnType,
  type Schema,
  schemaFromEntries,
} from '../../../schema/types.ts';
import { type Cell, Dataset, MaterializedDataset } from '../../../value.ts';

// biome-ignore lint/style/useNamingConvention: $-prefixed engine reserved
const ColSymbol = z.object({ $sym: z.string().min(1) });

export const unnest = z
  .object({
    name: z.literal('unnest'),
    params: z.tuple([z.array(ColSymbol).min(1)]),
    pick: z.array(ColSymbol).optional(),
    as: z.array(ColSymbol).optional(),
  })
  .describe(
    'Flatten nested-dataset columns into outer rows. `:pick` keeps only the listed inner columns (single column: any inner cols, in inner order; multiple columns: the shared grid key is always kept and `:pick` selects each column’s non-key inner cols). `:as` positionally renames the resulting columns. Single column: `:as` length = kept inner-column count. Multiple columns: zip-aligned by the shared leading inner column (the time-bucket grid key), union of key values null-filled; `:as` spans `[key, …each column’s kept non-key cols]`. A `CogniteTimeSeries` ref column is itself a nested dataset of RAW datapoints whose inner columns are `timestamp`/`value`/`isString`/`unit`; unnest it to obtain raw, unaggregated per-sample rows — e.g. `(transform ds (unnest [priorityOilRateStd] :pick [timestamp value] :as [ts oil]))`. This is the way to get raw points; reach for `time-bucket` instead only when you want aggregated buckets (mean/last/sum), never to retrieve raw samples.',
  )
  .register(pipelineVerbs, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(transform ds (unnest [oilProd gasProd] :pick [value] :as [day oil gas]))' },
    apply: async (data, dataset: MaterializedDataset, ctx: SpecialVerbCtx): Promise<MaterializedDataset> => {
      const callPos = opCallPos(ctx.spans, ctx.path);
      const cols = (data.params[0] as readonly z.infer<typeof ColSymbol>[]).map((s) => s.$sym);
      const pickNames = (data.pick ?? []).map((s) => s.$sym);
      const asNames = (data.as ?? []).map((s) => s.$sym);
      if (cols.length === 1) {
        return unnestOneColumn(dataset, cols[0] as string, pickNames, asNames, ctx, callPos);
      }
      return unnestZip(dataset, cols, pickNames, asNames, ctx, callPos);
    },
    propagateSchema: (data, ctx: PropagateSchemaCtx): Schema => {
      const parsed = data as z.infer<typeof unnest>;
      const cols = (parsed.params[0] as readonly z.infer<typeof ColSymbol>[]).map((s) => s.$sym);
      const pickNames = (parsed.pick ?? []).map((s) => s.$sym);
      const asNames = (parsed.as ?? []).map((s) => s.$sym);
      return cols.length === 1
        ? propagateUnnestOneColumn(ctx.inputSchema, cols[0] as string, pickNames, asNames)
        : propagateUnnestZip(ctx.inputSchema, cols, pickNames, asNames);
    },
    // No `referencedColumns` — unnest CONSUMES its column args at the
    // outer level rather than "uses" them. Reporting them would
    // trigger the dispatch wrapper's per-cell lift, which is exactly
    // what unnest must NOT do (it materialises + flattens those
    // cells itself). Column existence + nested-dataset typing are
    // validated inside `apply`.
  });

/**
 * Restrict a list of inner columns to the `:pick` selection,
 * preserving inner (schema) order. Identity when `:pick` is absent.
 * Element-shape-agnostic via `nameOf` so it is the single SSOT for
 * BOTH the name-list (single-column) and `[name, type]`-entry (zip)
 * paths and their `propagateSchema` twins.
 */
const applyPick = <T>(items: readonly T[], pick: readonly string[], nameOf: (item: T) => string): readonly T[] =>
  pick.length === 0 ? items : items.filter((item) => pick.includes(nameOf(item)));

/**
 * Validate every `:pick` name exists in `available`; throws a precise
 * `ShapeError` listing the offenders + the available set otherwise.
 */
const validatePick = (
  pick: readonly string[],
  available: readonly string[],
  label: string,
  callPos: SourcePos,
): void => {
  const set = new Set(available);
  const missing = pick.filter((n) => !set.has(n));
  if (missing.length > 0) {
    throw new ShapeError(
      `:pick names [${missing.join(', ')}] are not ${label}; available: [${available.join(', ')}]`,
      callPos,
      ':pick',
    );
  }
};
const unnestOneColumn = async (
  dataset: MaterializedDataset,
  col: string,
  pickNames: readonly string[],
  asNames: readonly string[],
  ctx: SpecialVerbCtx,
  callPos: SourcePos,
): Promise<MaterializedDataset> => {
  const colType = dataset.schema.get(col);
  if (colType === undefined) {
    throw new TypeMismatchError(
      `unnest column \`${col}\` to exist on the threaded dataset`,
      'no such column',
      'unnest',
      callPos,
    );
  }
  if (colType.kind !== 'nested-dataset') {
    throw new UnnestableColumnTypeError(col, describeColumnType(colType), callPos);
  }

  const innerSchema = colType.innerSchema;
  const allInner = Array.from(innerSchema.keys());
  validatePick(pickNames, allInner, `inner columns of \`${col}\``, callPos);
  // `:pick` keeps a subset of inner columns (inner order preserved).
  const innerColNames = applyPick(allInner, pickNames, (n) => n);
  // `:as` (if supplied) must match the kept inner-column count exactly.
  if (asNames.length > 0 && asNames.length !== innerColNames.length) {
    throw new TypeMismatchError(
      `:as length ${innerColNames.length} (${pickNames.length > 0 ? 'picked' : 'inner-schema'} column count)`,
      `received ${asNames.length}`,
      'unnest',
      callPos,
    );
  }
  const renamedInner: readonly string[] = asNames.length > 0 ? asNames : innerColNames;
  const renameMap = new Map(innerColNames.map((orig, i) => [orig, renamedInner[i] as string]));

  // Detect collisions between renamed inner cols and outer cols
  // (excluding the column being unnested, which is being replaced).
  const outerSurvivingCols = Array.from(dataset.schema.keys()).filter((c) => c !== col);
  const outerSet = new Set(outerSurvivingCols);
  for (const renamed of renamedInner) {
    if (outerSet.has(renamed)) {
      throw new UnnestCollisionError(col, renamed, callPos);
    }
  }

  const outerRows = materializedToRows(dataset);
  // Materialise every per-cell nested Dataset in ONE microtask window
  // (mirrors `liftPerCell`'s `Promise.all`) so the `ts-ref` source
  // fetcher's `queueMicrotask` batcher coalesces all cells into a
  // single CDF round-trip. A serial `for await` here drains the
  // microtask queue between cells, flushing that batcher once per row
  // (one request per row instead of one per unnest). `materializeIfLazy`
  // short-circuits already-materialised cells, so non-lazy inner data
  // stays free.
  const matCells = await Promise.all(
    outerRows.map((outerRow) => {
      const cell = outerRow[col];
      return cell instanceof Dataset
        ? materializeIfLazy(cell, ctx.evalCtx, ctx.ops, ctx.verbs)
        : Promise.resolve(undefined);
    }),
  );
  const newRows: Record<string, unknown>[] = [];
  outerRows.forEach((outerRow, ri) => {
    const matCell = matCells[ri];
    if (matCell === undefined) {
      return; // null / missing cell → no output rows
    }
    const innerRows = materializedToRows(matCell);
    for (const innerRow of innerRows) {
      const merged: Record<string, unknown> = {};
      for (const k of outerSurvivingCols) {
        merged[k] = outerRow[k];
      }
      // Emit ONLY the kept (picked) inner cols, renamed.
      for (const orig of innerColNames) {
        merged[renameMap.get(orig) ?? orig] = innerRow[orig];
      }
      newRows.push(merged);
    }
  });

  // Build output columns from merged rows.
  const outputColOrder = [...outerSurvivingCols, ...renamedInner];
  const outputCols: Record<string, Cell[]> = {};
  for (const k of outputColOrder) {
    outputCols[k] = newRows.map((r) => (r[k] ?? null) as Cell);
  }

  return new MaterializedDataset(outputCols, propagateUnnestOneColumn(dataset.schema, col, pickNames, asNames));
};

const propagateUnnestOneColumn = (
  inputSchema: Schema,
  col: string,
  pickNames: readonly string[],
  asNames: readonly string[],
): Schema => {
  const colType = inputSchema.get(col);
  if (colType === undefined || colType.kind !== 'nested-dataset') {
    // Schema-level mismatch surfaces at runtime via apply's throw;
    // here we fall back to the input schema unchanged.
    return inputSchema;
  }
  const innerSchema = colType.innerSchema;
  const allInner = Array.from(innerSchema.keys());
  const innerColNames = applyPick(allInner, pickNames, (n) => n);
  const renamedInner = asNames.length > 0 ? asNames : innerColNames;
  const entries: [string, ColumnType][] = [];
  for (const [k, v] of inputSchema.entries()) {
    if (k === col) {
      continue;
    }
    entries.push([k, v]);
  }
  innerColNames.forEach((orig, i) => {
    const renamed = renamedInner[i] ?? orig;
    const t = innerSchema.get(orig);
    if (t !== undefined) {
      entries.push([renamed, t]);
    }
  });
  return schemaFromEntries(entries);
};

/**
 * Resolve the shared leading inner column ("grid key") across the
 * listed columns. Every column must be `nested-dataset` and its inner
 * schema must start with a byte-identical `[name, type]` entry — the
 * `bucket` column produced by per-cell `time-bucket`. Returns the key
 * `[name, type]` plus, per column, its ordered non-key inner entries.
 * Throws the precise `ShapeError` / column-type error on violation.
 */
const resolveZipKey = (
  inputSchema: Schema,
  cols: readonly string[],
  callPos: SourcePos,
): {
  readonly keyName: string;
  readonly keyType: ColumnType;
  readonly nonKey: readonly (readonly [string, ColumnType])[][];
} => {
  let key: readonly [string, ColumnType] | undefined;
  const nonKey: (readonly [string, ColumnType])[][] = [];
  for (const col of cols) {
    const colType = inputSchema.get(col);
    if (colType === undefined) {
      throw new TypeMismatchError(
        `unnest column \`${col}\` to exist on the threaded dataset`,
        'no such column',
        'unnest',
        callPos,
      );
    }
    if (colType.kind !== 'nested-dataset') {
      throw new UnnestableColumnTypeError(col, describeColumnType(colType), callPos);
    }
    const innerEntries = Array.from(colType.innerSchema.entries());
    const lead = innerEntries[0];
    if (lead === undefined) {
      throw new ShapeError(
        `multi-column unnest requires column \`${col}\` to have a leading grid-key inner column`,
        callPos,
        '#0',
      );
    }
    if (key === undefined) {
      key = lead;
    } else if (lead[0] !== key[0] || !columnTypesEqual(lead[1], key[1])) {
      throw new ShapeError(
        `multi-column unnest requires the listed columns to share an identical leading inner column (the time-bucket grid key); \`${col}\` leads with \`${lead[0]}\` but expected \`${key[0]}\``,
        callPos,
        '#0',
      );
    }
    nonKey.push(innerEntries.slice(1));
  }
  const resolved = key as readonly [string, ColumnType];
  return { keyName: resolved[0], keyType: resolved[1], nonKey };
};

/** Stable equality/sort key for a grid-key cell value. */
const keyOf = (v: unknown): number | string => (v instanceof Date ? v.getTime() : (v as number | string));

const unnestZip = async (
  dataset: MaterializedDataset,
  cols: readonly string[],
  pickNames: readonly string[],
  asNames: readonly string[],
  ctx: SpecialVerbCtx,
  callPos: SourcePos,
): Promise<MaterializedDataset> => {
  const { keyName, nonKey } = resolveZipKey(dataset.schema, cols, callPos);

  // `:pick` keeps each column's non-key inner cols (the shared grid
  // key is ALWAYS kept). Must exist in every listed column.
  cols.forEach((c, ci) => {
    validatePick(
      pickNames,
      (nonKey[ci] as readonly (readonly [string, ColumnType])[]).map(([n]) => n),
      `non-key inner columns of \`${c}\``,
      callPos,
    );
  });
  const effNonKey: readonly (readonly (readonly [string, ColumnType])[])[] = nonKey.map((e) =>
    applyPick(e as readonly (readonly [string, ColumnType])[], pickNames, ([n]) => n),
  );

  // Output layout: shared key, then each column's kept non-key inner
  // cols in vector order. `:as` (if any) renames positionally;
  // otherwise the original inner names are kept (collision-checked).
  const baseNames = [keyName, ...effNonKey.flatMap((entries) => entries.map(([n]) => n))];
  if (asNames.length > 0 && asNames.length !== baseNames.length) {
    throw new ShapeError(
      `:as expects ${baseNames.length} names (1 shared key + Σ kept non-key inner cols), received ${asNames.length}`,
      callPos,
      ':as',
    );
  }
  const finalNames = asNames.length > 0 ? asNames : baseNames;

  const colSet = new Set(cols);
  const outerSurvivingCols = Array.from(dataset.schema.keys()).filter((c) => !colSet.has(c));
  const outerSet = new Set(outerSurvivingCols);
  const seen = new Set<string>();
  for (const n of finalNames) {
    if (outerSet.has(n) || seen.has(n)) {
      throw new UnnestCollisionError(cols.join(','), n, callPos);
    }
    seen.add(n);
  }

  const outerRows = materializedToRows(dataset);
  // Materialise EVERY (row, column) nested cell in ONE microtask
  // window so the `ts-ref` source fetcher's `queueMicrotask` batcher
  // coalesces them into a single CDF round-trip. A serial `for await`
  // over rows flushes that batcher once per row — see the matching
  // note in `unnestOneColumn`. `materializeIfLazy` short-circuits
  // already-materialised cells.
  const perColByRow = await Promise.all(
    outerRows.map((outerRow) =>
      Promise.all(
        cols.map(async (col) => {
          const cell = outerRow[col];
          if (!(cell instanceof Dataset)) {
            return new Map<number | string, Record<string, unknown>>();
          }
          const matCell = await materializeIfLazy(cell, ctx.evalCtx, ctx.ops, ctx.verbs);
          const byKey = new Map<number | string, Record<string, unknown>>();
          for (const r of materializedToRows(matCell)) {
            byKey.set(keyOf(r[keyName]), r);
          }
          return byKey;
        }),
      ),
    ),
  );
  const newRows: Record<string, unknown>[] = [];
  outerRows.forEach((outerRow, ri) => {
    const perCol = perColByRow[ri] as readonly Map<number | string, Record<string, unknown>>[];
    // Union of key values across columns (ascending, original value).
    const unionKeys = new Map<number | string, unknown>();
    cols.forEach((_, ci) => {
      const matCellRows = perCol[ci] as Map<number | string, Record<string, unknown>>;
      for (const r of matCellRows.values()) {
        unionKeys.set(keyOf(r[keyName]), r[keyName]);
      }
    });
    const orderedKeys = Array.from(unionKeys.entries()).sort(([a], [b]) => {
      if (a < b) {
        return -1;
      }
      return a > b ? 1 : 0;
    });

    for (const [kk, keyValue] of orderedKeys) {
      // Assemble POSITIONALLY into `finalNames`: inner names can
      // collide across columns (e.g. two `value`s), so a name-keyed
      // merge would drop slots — the slot index is the SSOT.
      const merged: Record<string, unknown> = {};
      for (const oc of outerSurvivingCols) {
        merged[oc] = outerRow[oc];
      }
      let slot = 0;
      merged[finalNames[slot++] as string] = keyValue;
      cols.forEach((_, ci) => {
        const row = (perCol[ci] as Map<number | string, Record<string, unknown>>).get(kk);
        for (const [n] of effNonKey[ci] as readonly (readonly [string, ColumnType])[]) {
          merged[finalNames[slot++] as string] = row === undefined ? null : row[n];
        }
      });
      newRows.push(merged);
    }
  });

  const outputColOrder = [...outerSurvivingCols, ...finalNames];
  const outputCols: Record<string, Cell[]> = {};
  for (const k of outputColOrder) {
    outputCols[k] = [];
  }
  for (const r of newRows) {
    for (const k of outputColOrder) {
      (outputCols[k] as Cell[]).push((r[k] ?? null) as Cell);
    }
  }
  return new MaterializedDataset(outputCols, propagateUnnestZip(dataset.schema, cols, pickNames, asNames));
};

const propagateUnnestZip = (
  inputSchema: Schema,
  cols: readonly string[],
  pickNames: readonly string[],
  asNames: readonly string[],
): Schema => {
  // Validate without throwing — apply surfaces the precise error.
  let key: readonly [string, ColumnType] | undefined;
  const nonKey: (readonly [string, ColumnType])[][] = [];
  for (const col of cols) {
    const colType = inputSchema.get(col);
    if (colType === undefined || colType.kind !== 'nested-dataset') {
      return inputSchema;
    }
    const innerEntries = Array.from(colType.innerSchema.entries());
    const lead = innerEntries[0];
    if (lead === undefined) {
      return inputSchema;
    }
    if (key === undefined) {
      key = lead;
    } else if (lead[0] !== key[0] || !columnTypesEqual(lead[1], key[1])) {
      return inputSchema;
    }
    nonKey.push([...applyPick(innerEntries.slice(1), pickNames, ([n]) => n)]);
  }
  const resolvedKey = key as readonly [string, ColumnType];
  const baseEntries: [string, ColumnType][] = [
    [resolvedKey[0], resolvedKey[1]],
    ...nonKey.flatMap((entries) => entries.map(([n, t]) => [n, t] as [string, ColumnType])),
  ];
  const colSet = new Set(cols);
  const entries: [string, ColumnType][] = [];
  for (const [k, v] of inputSchema.entries()) {
    if (!colSet.has(k)) {
      entries.push([k, v]);
    }
  }
  baseEntries.forEach(([orig, t], i) => {
    entries.push([asNames.length > 0 ? (asNames[i] as string) : orig, t]);
  });
  return schemaFromEntries(entries);
};
